import {
  ActionIcon,
  Badge,
  CopyButton,
  Group,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import {
  IconBrain,
  IconCheck,
  IconCopy,
  IconMessage,
  IconPlayerStopFilled,
} from "@tabler/icons-react";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import ReactTimeago from "react-timeago-i18n";
import StatusBadge from "./StatusBadge";
import ProgressBadge from "./ProgressBadge";
import { cancelRun } from "../api";

/**
 * Identity for the run being inspected: what it was, how it ended, where it ran.
 *
 * This is the modal's title. It used to be a grey card floating at the top of the right-hand
 * rail, which left the dialog itself untitled and made the run's name look like just another
 * panel. Environment moves up here too — it was buried in a collapsed "Details" disclosure
 * despite being a first-class filter on the list behind this modal.
 */
export default function LogDetailHeader({ log }) {
  if (!log) return null;

  const running = log.status?.toLowerCase() === "pending";
  const stopAsked = !!log.cancelRequestedAt;

  const confirmStop = () =>
    modals.openConfirmModal({
      title: "Stop this run?",
      children: (
        <Stack gap="xs">
          <Text size="sm">
            This stops the run and every sub-agent beneath it. It takes effect at the next step —
            a model call already in flight finishes first.
          </Text>
          <Text size="sm" c="dimmed">
            What it has produced so far is kept and still billed.
          </Text>
        </Stack>
      ),
      labels: { confirm: "Stop run", cancel: "Keep running" },
      confirmProps: { color: "red" },
      onConfirm: () =>
        cancelRun(log.id).then((r) =>
          notifications.show({
            title: r.signalled === 0 ? "Nothing to stop" : "Stopping",
            message: r.message,
            color: r.signalled === 0 ? "gray" : "orange",
          }),
        ),
    });

  const isAgent = log.agentRole || log.agentDepth > 0;
  const title = log.agentRole || log.flowName || "Request";
  // Only worth repeating when it isn't already the title.
  const subtitleFlow = log.agentRole && log.flowName ? log.flowName : null;

  return (
    <Group gap="sm" wrap="nowrap" align="flex-start">
      <ThemeIcon size="lg" variant="light" color={isAgent ? "orange" : "blue"}>
        {isAgent ? <IconBrain size={18} /> : <IconMessage size={18} />}
      </ThemeIcon>
      <Stack gap={4} style={{ minWidth: 0 }}>
        <Group gap="xs" wrap="wrap">
          <Text fz={{ base: "md", sm: "lg" }} fw={600} lh={1.2}>
            {title}
          </Text>
          <StatusBadge
            status={log.status}
            warnings={log.warnings}
            warningCount={log.warningCount}
            cancelledAt={log.cancelledAt}
            cancelRequestedAt={log.cancelRequestedAt}
            failureReason={log.failureReason}
          />
          <ProgressBadge
            score={log.progressScore}
            certainty={log.progressCertainty}
            lowest={log.minProgressScore}
            readings={log.progressScoreCount}
            verifier={log.verifierModel}
          />
          {running && (
            <Tooltip
              label={stopAsked ? "Already stopping" : "Stop this run and its sub-agents"}
              withArrow
            >
              <ActionIcon
                size="sm"
                color="red"
                variant="subtle"
                disabled={stopAsked}
                onClick={confirmStop}
                aria-label="Stop run"
              >
                <IconPlayerStopFilled size={14} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
        <Group gap="xs" wrap="wrap">
          {subtitleFlow && (
            <Text fz="xs" c="dimmed">
              {subtitleFlow}
            </Text>
          )}
          {log.environment && (
            <Badge size="xs" variant="default" tt="none" radius="sm">
              {log.environment}
            </Badge>
          )}
          {log.isDebug && (
            <Badge size="xs" color="yellow" variant="light" tt="none" radius="sm">
              debug
            </Badge>
          )}
          {log.detailDropped && (
            <Tooltip
              label="This run missed the tenant's sample, so the full prompt and answer were never stored. Cost, model and status are still here."
              withArrow
            >
              <Badge size="xs" color="gray" variant="light" tt="none" radius="sm">
                text not retained
              </Badge>
            </Tooltip>
          )}
          {log.agentDepth > 0 && (
            <Text fz="xs" c="dimmed">
              depth {log.agentDepth}
            </Text>
          )}
          <Text fz="xs" c="dimmed">
            {log.type}
          </Text>
          {log.sent && (
            <Text fz="xs" c="dimmed">
              <ReactTimeago date={log.sent} />
            </Text>
          )}
          {/* A 24-character hex id is a quarter of a phone screen and nothing you read there;
              it is a thing you copy, on the machine you are going to paste it into. */}
          <Group gap={2} wrap="nowrap" visibleFrom="sm">
            <Text fz="xs" c="dimmed" ff="monospace">
              {log.id}
            </Text>
            <CopyButton value={log.id} timeout={1500}>
              {({ copied, copy }) => (
                <Tooltip label={copied ? "Copied" : "Copy request ID"} withArrow>
                  <ActionIcon
                    size="xs"
                    variant="subtle"
                    color={copied ? "teal" : "gray"}
                    onClick={copy}
                  >
                    {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
          </Group>
        </Group>
      </Stack>
    </Group>
  );
}
